import { motion } from "framer-motion";
import { Printer, Rocket, Target, Clock, Gamepad2, BookOpen, Wrench, ListChecks, Sparkles, GraduationCap, CheckCircle2 } from "lucide-react";
import { levelStyle } from "@/lib/constants";

const fade = (d = 0) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: d } });

function Chip({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 bg-white/80 backdrop-blur rounded-2xl px-4 py-3 border border-white shadow-sm">
      <span className="grid place-items-center w-9 h-9 rounded-xl bg-slate-900 text-white shrink-0"><Icon className="w-4 h-4" /></span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="text-sm font-bold text-slate-800 truncate">{value}</p>
      </div>
    </div>
  );
}

export default function MissionSheet({ fiche }) {
  if (!fiche) return null;
  const f = fiche;
  const s = levelStyle(f.level || "3e");

  return (
    <div className="bg-slate-50 pb-16">
      <div className="no-print max-w-5xl mx-auto px-4 sm:px-6 pt-6 flex items-center justify-end">
        <button onClick={() => window.print()} data-testid="fiche-print" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white font-bold text-sm hover:bg-slate-700 transition-colors">
          <Printer className="w-4 h-4" /> Imprimer / PDF
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-5">
        <div className="fiche-sheet bg-white rounded-[2rem] shadow-xl shadow-slate-900/5 overflow-hidden border border-slate-100">
          <div className={`relative px-8 sm:px-12 py-10 ${s.bg} overflow-hidden`}>
            <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-white/40 blur-2xl" />
            <div className="absolute right-10 bottom-4 opacity-20"><Sparkles className="w-24 h-24 text-slate-900" /></div>
            <div className="relative">
              <span className={`inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-[0.15em] ${s.text}`}>
                <GraduationCap className="w-4 h-4" /> {s.label} · Fiche de mission
              </span>
              <motion.h1 {...fade(0.05)} className="font-heading font-black text-4xl sm:text-5xl text-slate-900 mt-3 leading-tight">{f.title}</motion.h1>
              {f.hero && <motion.p {...fade(0.12)} className="mt-2 text-lg font-bold text-slate-700">{f.hero}</motion.p>}
            </div>
          </div>

          <div className="px-8 sm:px-12 py-10 space-y-10">
            <motion.div {...fade(0.05)} className="relative rounded-3xl bg-gradient-to-br from-amber-50 to-amber-100/60 border border-amber-200 p-6 sm:p-8">
              <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-amber-700"><Rocket className="w-4 h-4" /> Mission du jour</span>
              <p className="mt-3 text-slate-800 text-lg leading-relaxed">{f.mission}</p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Chip icon={Clock} label="Durée" value={f.duration || "90 min"} />
              <Chip icon={Gamepad2} label="Type" value={f.type || "Atelier"} />
              <Chip icon={BookOpen} label="Référentiel" value={f.referentiel || f.competence} />
              <Chip icon={GraduationCap} label="Niveau" value={s.label} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-3xl border-2 border-sky-100 bg-sky-50/50 p-6">
                <span className="inline-flex items-center gap-2 text-sm font-black text-sky-700"><Target className="w-5 h-5" /> Objectif</span>
                <p className="mt-2 text-slate-700">{f.objectif}</p>
              </div>
              <div className="rounded-3xl border-2 border-emerald-100 bg-emerald-50/50 p-6">
                <span className="inline-flex items-center gap-2 text-sm font-black text-emerald-700"><CheckCircle2 className="w-5 h-5" /> Compétence visée</span>
                <p className="mt-2 text-slate-700">{f.competence}</p>
              </div>
            </div>

            {f.steps?.length > 0 && (
              <div>
                <h2 className="font-heading font-extrabold text-2xl text-slate-900 flex items-center gap-2 mb-5"><ListChecks className="w-6 h-6 text-sky-500" /> Le déroulé</h2>
                <ol className="space-y-3">
                  {f.steps.map((step, i) => (
                    <li key={step} className="flex gap-4 items-start bg-white rounded-2xl border border-slate-100 p-4">
                      <span className="grid place-items-center w-8 h-8 rounded-full bg-slate-900 text-white font-black text-sm shrink-0">{i + 1}</span>
                      <span className="text-slate-700 pt-1">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="rounded-3xl bg-slate-900 text-white p-6 sm:p-8">
              <span className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-wide text-sky-300"><Wrench className="w-4 h-4" /> Préparation</span>
              <p className="mt-2 text-slate-200">{f.preparation}</p>
              {f.colorCode?.length > 0 && (
                <>
                  <p className="mt-5 text-xs font-bold uppercase tracking-wide text-slate-400">Le code couleur de Roby</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {f.colorCode.map((c) => (
                      <span key={c.label} className="inline-flex items-center gap-2 bg-white/10 rounded-full pl-2 pr-4 py-1.5 text-sm font-semibold">
                        <span className={`w-4 h-4 rounded-full ${c.cls}`} /> {c.color} = {c.label}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div>
              <h2 className="font-heading font-extrabold text-2xl text-slate-900 flex items-center gap-2 mb-5"><Wrench className="w-6 h-6 text-sky-500" /> Matériel nécessaire</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-3xl border border-slate-100 bg-white p-6">
                  <p className="font-bold text-slate-900 mb-3">💻 Pour les élèves</p>
                  <ul className="space-y-2">
                    {(f.materialDigital || []).map((m) => (<li key={m} className="flex gap-2 text-slate-600 text-sm"><span className="text-sky-500 mt-0.5">▸</span> {m}</li>))}
                  </ul>
                </div>
                <div className="rounded-3xl border border-slate-100 bg-white p-6">
                  <p className="font-bold text-slate-900 mb-3">🎯 Pour l'enseignant · l'animateur</p>
                  <ul className="space-y-2">
                    {(f.materialTeacher || []).map((m) => (<li key={m} className="flex gap-2 text-slate-600 text-sm"><span className="text-sky-500 mt-0.5">▸</span> {m}</li>))}
                  </ul>
                </div>
              </div>
            </div>

            {f.synthese?.length > 0 && (
              <div className="rounded-3xl border-2 border-indigo-100 bg-indigo-50/50 p-6 sm:p-8">
                <span className="inline-flex items-center gap-2 text-sm font-black text-indigo-700"><BookOpen className="w-5 h-5" /> Synthèse</span>
                <ul className="mt-3 space-y-2">
                  {f.synthese.map((m) => (<li key={m} className="flex gap-2 text-slate-700"><span className="text-indigo-500 mt-0.5">▸</span> {m}</li>))}
                </ul>
              </div>
            )}

            {f.exercices?.length > 0 && (
              <div>
                <h2 className="font-heading font-extrabold text-2xl text-slate-900 flex items-center gap-2 mb-5"><ListChecks className="w-6 h-6 text-amber-500" /> Exercices</h2>
                <ol className="space-y-3">
                  {f.exercices.map((ex, i) => (
                    <li key={ex} className="flex gap-4 items-start bg-amber-50/60 rounded-2xl border border-amber-100 p-4">
                      <span className="grid place-items-center w-8 h-8 rounded-full bg-amber-400 text-white font-black text-sm shrink-0">{i + 1}</span>
                      <span className="text-slate-700 pt-1">{ex}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <div className={`px-8 sm:px-12 py-5 ${s.bg} flex items-center justify-between`}>
            <span className="font-heading font-black text-slate-900">Kwarizmix</span>
            <span className="text-xs font-semibold text-slate-500">{f.title} · {s.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
